import {
  CloseOutlined,
  CopyFilled,
  LinkOutlined,
  PauseOutlined,
  UndoOutlined,
  DownOutlined,
  SettingOutlined,
  FileOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  message,
  Tooltip,
  Tag,
  Typography,
  Descriptions,
  Space,
  Alert,
} from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import Config from '../app/config';
import ErrorResult from '../components/ErrorResult';
import Page from '../components/Page';
import PageTitle from '../components/PageTitle';
import PermissionChecker from '../components/PermissionChecker';
import VacancyActionsModal, { VacancyActions } from '../components/VacancyModal/VacancyActions';
import useCanAccessJobPosting from '../hooks/useCanAccessJobPosting';
import usePermissionCheckerQuery from '../hooks/usePermissionCheckerQuery';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { JobPostingActionsProps } from '../pages/Opportunities';
import { JobPostingDetail, JobPostingId } from '../types';
import { calculateOpenDays } from '../utils';
import styles from './ShowOpportunityNavigator.module.css';

const ShowOpportunityNavigator = () => {
  const navigate = useTypedNavigate();
  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const [selected, setSelected] = useState<JobPostingActionsProps>();

  const { t } = useTranslation();

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const {
    canManage,
    canView,
    isLoading: canAccessLoading,
  } = useCanAccessJobPosting({
    jobPostingId: id,
  });

  const functions = useAPIQuery(['/mindsight/ats-integrations/functions/']);

  const functionName = functions.data?.results.find(
    (fn) => fn.id === jobPosting.data?.function_id
  )?.name;

  const recruitmentProcess = useAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        status: 'ACTIVE',
        job_posting: id,
      },
    },
  ]);

  const reopenVacancyMutation = useAPIMutation(
    async (data: { jobPostingId?: JobPostingDetail['id'] }, api) => {
      await api.post(`/job-postings/${data.jobPostingId}/reopen/`).then(() => {
        message.success(t('A vaga foi reaberta!'));
      });
      jobPosting.refetch();
    }
  );

  const handleClick = (action: VacancyActions) => {
    if (jobPosting.data) {
      if (action === 'REOPEN') {
        reopenVacancyMutation.mutate({
          jobPostingId: jobPosting.data?.id,
        });
      } else {
        setSelected({ action, jobPosting: jobPosting.data, open: true });
      }
    }
  };

  const hasCreateAndEditVacanciesPermission = usePermissionCheckerQuery([
    'create_and_edit_vacancies',
  ]);

  return (
    <PermissionChecker
      condition={canView}
      isLoading={canAccessLoading}
      redirectTo=".."
      requiredPermissions={['view_all_vacancies']}
    >
      <Page>
        {selected?.jobPosting && (
          <VacancyActionsModal
            {...selected}
            onOk={() => {
              if (selected.action === 'DELETE') {
                navigate({ pathname: '/:tenant/opportunities/', params: { tenant } });
              } else {
                jobPosting.refetch();
              }
              setSelected({ ...selected, open: false });
            }}
            onCancel={() => setSelected({ ...selected, open: false })}
          />
        )}
        <ErrorResult error={jobPosting.error}>
          <PageTitle
            ghost={false}
            title={jobPosting.data?.name || '...'}
            subTitle={functionName}
            extra={
              <Space direction="horizontal">
                {canManage && jobPosting.data?.status === 'IN_PROGRESS' && (
                  <Dropdown.Button
                    type="primary"
                    icon={<DownOutlined />}
                    menu={{
                      onClick: ({ key }) => {
                        switch (key) {
                          case 'suspend':
                            handleClick('SUSPENDED');
                            break;
                          case 'cancel':
                            handleClick('CANCELED');
                            break;
                          case 'clone':
                            handleClick('CLONE');
                            break;
                          case 'delete':
                            handleClick('DELETE');
                            break;
                        }
                      },
                      items: [
                        { key: 'suspend', icon: <PauseOutlined />, label: t('Congelar') },
                        { key: 'cancel', icon: <CloseOutlined />, label: t('Cancelar') },
                        { key: 'clone', icon: <CopyFilled />, label: t('Clonar') },
                        {
                          key: 'delete',
                          danger: true,
                          disabled: (recruitmentProcess.data?.count ?? 1) > 0,
                          icon: <DeleteOutlined />,
                          label: (
                            <Tooltip
                              title={
                                (recruitmentProcess.data?.count ?? 1) > 0
                                  ? t('Essa vaga não pode ser excluída, pois possui candidatos.')
                                  : ''
                              }
                            >
                              {' '}
                              Excluir
                            </Tooltip>
                          ),
                        },
                      ],
                    }}
                    onClick={() => {
                      if (jobPosting.data) {
                        setSelected({
                          action: 'FINALIZED',
                          jobPosting: jobPosting.data,
                          open: true,
                        });
                      }
                    }}
                  >
                    Concluir
                  </Dropdown.Button>
                )}
                {canManage && ['CANCELED', 'SUSPENDED'].includes(jobPosting.data?.status || '') && (
                  <Dropdown.Button
                    menu={{
                      onClick: ({ key }) => {
                        switch (key) {
                          case 'delete':
                            handleClick('DELETE');
                            break;
                        }
                      },
                      items: [
                        {
                          key: 'delete',
                          danger: true,
                          disabled: (recruitmentProcess.data?.count ?? 1) > 0,
                          icon: <DeleteOutlined />,
                          label: (
                            <Tooltip
                              title={
                                (recruitmentProcess.data?.count ?? 1) > 0
                                  ? t('Essa vaga não pode ser excluída, pois possui candidatos.')
                                  : ''
                              }
                            >
                              Excluir
                            </Tooltip>
                          ),
                        },
                      ],
                    }}
                    onClick={() => {
                      if (jobPosting.data) {
                        handleClick('REOPEN');
                      }
                    }}
                  >
                    <UndoOutlined />
                    Reabrir
                  </Dropdown.Button>
                )}
                <Dropdown
                  key="more"
                  menu={{
                    onClick: ({ key }) => {
                      switch (key) {
                        case 'change':
                          navigate({
                            pathname: '/:tenant/opportunities/:id/change/',
                            params: { tenant, id },
                          });
                          break;
                        case 'link':
                          window.open(`${Config.REACT_APP_CAREER_PAGE_URL}${tenant}/${id}`);
                          break;
                        case 'summary':
                          navigate({
                            pathname: '/:tenant/opportunities/:id/details/',
                            params: { tenant, id },
                          });
                          break;
                      }
                    },
                    items: [
                      canManage && hasCreateAndEditVacanciesPermission.data
                        ? {
                            key: 'change',
                            icon: <EditOutlined />,
                            label: t('Editar Vaga'),
                          }
                        : null,
                      {
                        key: 'link',
                        icon: <LinkOutlined />,
                        label:
                          jobPosting.data?.external_publication ||
                          jobPosting.data?.unlisted_external_publication ? (
                            <Trans>Link Da Vaga</Trans>
                          ) : (
                            <Tooltip
                              title={t(
                                'Não é possível fornecer o link da vaga pois ela não está publicada'
                              )}
                            >
                              <Trans>Link da Vaga</Trans>
                            </Tooltip>
                          ),
                        disabled:
                          !jobPosting.data?.external_publication &&
                          !jobPosting.data?.unlisted_external_publication,
                      },
                      {
                        key: 'summary',
                        icon: <FileOutlined />,
                        label: t('Resumo Da Vaga'),
                      },
                    ],
                  }}
                  placement="bottomRight"
                >
                  <Button type="ghost" icon={<SettingOutlined />} />
                </Dropdown>
              </Space>
            }
          >
            <Descriptions size="small" column={6} layout="horizontal">
              <Descriptions.Item label="Status">
                {jobPosting.data?.status === 'IN_PROGRESS' &&
                  (calculateOpenDays(jobPosting.data?.created_at) <=
                    (jobPosting.data?.limit_days || 0) ||
                    jobPosting.data?.limit_days === undefined) && (
                    <Tag color="blue">
                      <Trans>Em andamento</Trans>
                    </Tag>
                  )}
                {jobPosting.data?.created_at &&
                  jobPosting.data?.status === 'IN_PROGRESS' &&
                  calculateOpenDays(jobPosting.data?.created_at) >
                    (jobPosting.data?.limit_days || 0) && (
                    <Tag color="error">
                      <Trans>Atrasada</Trans>
                    </Tag>
                  )}
                {jobPosting.data?.status === 'SUSPENDED' && (
                  <Tag>
                    <Trans>Congelada</Trans>
                  </Tag>
                )}
                {jobPosting.data?.status === 'CANCELED' && (
                  <Tag>
                    <Trans>Cancelada</Trans>
                  </Tag>
                )}
                {jobPosting.data?.status === 'FINALIZED' && (
                  <Tag color="success">
                    <Trans>Concluída</Trans>
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="SLA">
                <Tag>
                  <Typography.Text>
                    {jobPosting.data?.limit_days ?? 0} <Trans>dias</Trans>
                  </Typography.Text>
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('Dias em aberto')}>
                {jobPosting.data?.created_at && (
                  <Tag>
                    <Typography.Text>
                      {calculateOpenDays(
                        jobPosting.data?.created_at,
                        jobPosting.data?.finalization_date
                      )}{' '}
                      dias
                    </Typography.Text>
                  </Tag>
                )}
              </Descriptions.Item>
              {jobPosting.data?.status === 'FINALIZED' && (
                <Descriptions.Item contentStyle={{ justifyContent: 'flex-end' }}>
                  <Alert
                    className={styles.alert}
                    message={`${t('Concluída em')} ${moment(
                      jobPosting.data?.finalization_date
                    ).format('DD/MM/YYYY - HH:mm [GMT]Z')}`}
                    type="success"
                  />
                </Descriptions.Item>
              )}
            </Descriptions>
          </PageTitle>
        </ErrorResult>
        <Outlet />
      </Page>
    </PermissionChecker>
  );
};

export default ShowOpportunityNavigator;
